// src/utils/siteJobs.js
// ---------------------------------------------------------------------------
// Find every job at the same property, forgiving how the site name was typed.
//
// Firestore can only match a site name EXACTLY ("Liberty Grove Condominium"
// will not find "Liberty Grove Condominium " or "Liberty Grove Condominiums"),
// so this reads the jobs collection once and compares a normalized key:
// lowercase, punctuation/extra spaces removed, "&" = "and", simple plurals
// dropped. Jobs that share a propertyId also match.
//
// Read-only. Used by Change Dates (QuickJobFieldEditor) to line up visits.
// ---------------------------------------------------------------------------

import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

/** "Liberty Grove Condominiums, " -> "liberty grove condominium" */
export const siteKey = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w))
    .join(" ");

const siteNameOf = (j) => j?.siteName || j?.propertyName || j?.property || "";

/** True when two jobs are at the same property. */
export const sameSite = (a, b) => {
  if (a?.propertyId && b?.propertyId && a.propertyId === b.propertyId) return true;
  const ka = siteKey(siteNameOf(a));
  return Boolean(ka) && ka === siteKey(siteNameOf(b));
};

/**
 * Every other (not deleted) job at this job's property. Throws on a read
 * failure so the caller can show it instead of an empty list.
 */
export const loadJobsAtSameSite = async (job) => {
  if (!job || (!siteKey(siteNameOf(job)) && !job.propertyId)) return [];
  const snap = await getDocs(collection(db, "jobs"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((j) => j.id !== job.id && String(j.status || "").toLowerCase() !== "deleted" && sameSite(job, j));
};
